import { useCallback } from "react";
import type { AppNotification, Period, Role, Settings } from "@/types";
import type { StoreCtx } from "@/store/appTypes";
import { PERIOD_KEY, ROLE_KEY } from "@/store/appTypes";
import {
  saveNotification,
  saveSettings,
  setAllNotificationsRead,
  setNotificationRead,
  updateProfileContact as apiUpdateProfileContact,
  type ProfileContact,
} from "@/store/api";

// Acciones base de la app: rol, período, ajustes, notificaciones y perfil.
export const useCoreActions = (ctx: StoreCtx) => {
  const { data, setData, run, refresh, setError, user, refreshProfile } = ctx;

  const setRole = useCallback(
    (role: Role) => {
      localStorage.setItem(ROLE_KEY, role);
      setData((p) => ({ ...p, role }));
    },
    [setData]
  );

  const setPeriod = useCallback(
    (period: Period) => {
      localStorage.setItem(PERIOD_KEY, JSON.stringify(period));
      setData((p) => ({ ...p, period }));
    },
    [setData]
  );

  const updateSettings = useCallback(
    (patch: Partial<Settings>) =>
      run(async () => {
        const next: Settings = { ...data.settings, ...patch };
        setData((p) => ({ ...p, settings: next }));
        await saveSettings(next);
      }),
    [run, data.settings, setData]
  );

  const pushNotification = useCallback(
    (n: Omit<AppNotification, "id" | "date" | "read">) =>
      run(async () => {
        const saved = await saveNotification({
          ...n,
          date: new Date().toISOString(),
          read: false,
        });
        setData((p) => ({ ...p, notifications: [saved, ...p.notifications] }));
      }),
    [run, setData]
  );

  const markNotificationRead = useCallback(
    (id: string) =>
      run(async () => {
        await setNotificationRead(id);
        setData((p) => ({
          ...p,
          notifications: p.notifications.map((x) => (x.id === id ? { ...x, read: true } : x)),
        }));
      }),
    [run, setData]
  );

  const markAllRead = useCallback(
    (audience: "admin" | "asesor") =>
      run(async () => {
        await setAllNotificationsRead(audience);
        setData((p) => ({
          ...p,
          notifications: p.notifications.map((x) =>
            x.audience === audience || x.audience === "all" ? { ...x, read: true } : x
          ),
        }));
      }),
    [run, setData]
  );

  const updateProfileContact = useCallback(
    (id: string, contact: ProfileContact): Promise<boolean> =>
      (async () => {
        try {
          await apiUpdateProfileContact(id, contact);
          if (id === user?.id) await refreshProfile?.();
          await refresh();
          setError(null);
          return true;
        } catch (e) {
          setError(e instanceof Error ? e.message : "No se pudieron guardar los datos.");
          return false;
        }
      })(),
    [refresh, refreshProfile, user, setError]
  );

  return {
    setRole,
    setPeriod,
    updateSettings,
    pushNotification,
    markNotificationRead,
    markAllRead,
    updateProfileContact,
  };
};